import {
  Assignment,
  AttachFile,
  Category,
  ChangeCircle,
  Comment,
  Edit,
  Email,
  EmailOutlined,
  Event,
  Group,
  MeetingRoom,
  Person,
  Phone,
  PhoneForwarded,
  Quiz,
  Replay,
  Send,
  Settings,
  Sms,
  Task,
  VideoCall,
  Work,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { ProcessedTimelineItem } from "../../types/Timeline.types";
import { useState } from "react";

interface TimelineItemProps {
  item: ProcessedTimelineItem;
  onItemClick?: (item: ProcessedTimelineItem) => void;
}

const TimelineItem = ({ item, onItemClick }: TimelineItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), "d MMM yyyy HH:mm", { locale: sv });
  };

  const getIcon = (iconName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      Assignment: <Assignment />,
      AttachFile: <AttachFile />,
      Category: <Category />,
      ChangeCircle: <ChangeCircle />,
      Comment: <Comment />,
      Edit: <Edit />,
      Email: <Email />,
      EmailOutlined: <EmailOutlined />,
      Event: <Event />,
      Group: <Group />,
      MeetingRoom: <MeetingRoom />,
      Person: <Person />,
      Phone: <Phone />,
      PhoneForwarded: <PhoneForwarded />,
      Quiz: <Quiz />,
      Replay: <Replay />,
      Send: <Send />,
      Settings: <Settings />,
      Sms: <Sms />,
      Task: <Task />,
      VideoCall: <VideoCall />,
      Work: <Work />,
    };
    return iconMap[iconName] || <Event />;
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "eventdata":
        return "Händelse";
      case "work":
        return "Ärende";
      case "eventlog":
        return "Systemlogg";
      case "workitem":
        return "Aktivitet";
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "eventdata":
        return "primary";
      case "work":
        return "success";
      case "eventlog":
        return "info";
      case "workitem":
        return "warning";
      default:
        return "default";
    }
  };

  const handleClick = () => {
    if (item.actionable && onItemClick) {
      onItemClick(item);
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        position: "relative",
        cursor: item.actionable ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": item.actionable
          ? {
              boxShadow: 3,
              transform: "translateY(-1px)",
            }
          : {},
        "&:before": {
          content: '""',
          position: "absolute",
          left: -20,
          top: "50%",
          transform: "translateY(-50%)",
          width: 3,
          height: "60%",
          bgcolor: item.color,
          borderRadius: "3px",
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          {/* Icon */}
          <Avatar
            sx={{
              bgcolor: item.color,
              width: 40,
              height: 40,
              mt: 0.5,
            }}
          >
            {getIcon(item.icon)}
          </Avatar>

          {/* Content */}
          <Box flex={1} minWidth={0}>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{
                    fontSize: "1rem",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {formatTimestamp(item.timestamp)}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1} ml={1}>
                <Chip
                  label={getTypeLabel(item.type)}
                  size="small"
                  color={
                    getTypeColor(item.type) as
                      | "default"
                      | "primary"
                      | "secondary"
                      | "error"
                      | "info"
                      | "success"
                      | "warning"
                  }
                  variant="outlined"
                />
                {item.actionable && isHovered && (
                  <Tooltip title="Klicka för att visa detaljer">
                    <IconButton size="small">
                      <Assignment fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.description}
            </Typography>

            {/* Metadata */}
            {item.metadata && (
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                <>
                  {item.metadata.createdBy && (
                    <Chip
                      label={`Av: ${item.metadata.createdBy}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                  )}
                  {item.metadata.assignedTo && (
                    <Chip
                      label={`Tilldelad: ${item.metadata.assignedTo}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                  )}
                  {item.metadata.team && (
                    <Chip
                      label={`Team: ${item.metadata.team}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                  )}
                  {item.metadata.category && (
                    <Chip
                      label={item.metadata.category as unknown as string}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                  )}
                  {item.metadata.hasUnansweredEmail && (
                    <Chip
                      label="Obesvarad e-post"
                      size="small"
                      color="warning"
                      sx={{ fontSize: "0.7rem", height: 20 }}
                    />
                  )}
                </>
              </Stack>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TimelineItem;
